import { ICellRendererParams } from '@ag-grid-community/core';
import moment from 'moment';

import { Document, DocumentLine } from '../../../../commercial-documents/models/document';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import {
	CommonColDef, DateColDef, IconRedirectColDef, LinkMultiValueColDef, MultiLineColDef, LinkColDef,
	NumericColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterOrderNumberFilterComponent } from '../../filter-type/datacenter-order-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DocumentService } from '../../../../commercial-documents/services/documents.service';
import { resolveStringCellValue } from '../../../utils/resolved-concatenated-value';
import { DatacenterDateRangeFilterComponent } from '../../filter-type/datacenter-date-range-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';

type PartialDocument = Partial<Omit<Document, 'documentLines'>> & { documentLines?: Partial<DocumentLine>[] };

function formatColumnValue(
	colId: string,
	document: PartialDocument | undefined
) {
	if (!colId || document?.documentLines === undefined) {
		return '';
	}
	switch (colId) {
	case 'mill':
		if (document.type && (document.type === 'CE' || document.type === 'DA')) {
			return document.millDescription;
		}
		return document.documentLines.map(item => item.millDescription)
			.filter((value, index, self) => self.indexOf(value) === index).join(';');
	case 'customer':
		if (document.type && (document.type === 'IN' || document.type === 'XCARB')) {
			return document.documentLines.filter(item => item.soldtoName && item.soldtoBusinessId)
				.map(item => `${ item.soldtoName } (${ item.soldtoBusinessId })`)
				.filter((value, index, self) => self.indexOf(value) === index)
				.join(';');
		}
		return `${ document.soldtoName } (${ document.soldtoBusinessId })`;
	case 'customerOrderNumber':
		if (document.type && (document.type === 'CE' || document.type === 'OA')) {
			return document.documentLines.map(item => {
				if (document.customerOrderReference !== undefined
					&& item.customerOrderItemReference !== undefined) {
					return `${ document.customerOrderReference }/${ item.customerOrderItemReference }`;
				} else if (document.customerOrderReference !== undefined
					&& item.customerOrderItemReference === undefined) {
					return document.customerOrderReference;
				}
				return '';
			}).filter((value, index, self) => self.indexOf(value) === index).join(';');
		}
		return document.documentLines.map(item => {
			if (item.customerOrderReference !== undefined
				&& item.customerOrderItemReference !== undefined) {
				return `${ item.customerOrderReference }/${ item.customerOrderItemReference }`;
			} else if (item.customerOrderReference !== undefined
				&& item.customerOrderItemReference === undefined) {
				return item.customerOrderReference;
			}
			return '';
		}).filter((value, index, self) => self.indexOf(value) === index).join(';');
	case 'supplierOrderNumber':
		if (document.type && (document.type === 'CE' || document.type === 'OA')) {
			return document.documentLines.map(item => {
				if (document.supplierOrderReference !== undefined
					&& item.supplierOrderReferenceItem !== undefined) {
					return `${ document.supplierOrderReference }/${ item.supplierOrderReferenceItem }`;
				} else if (document.supplierOrderReference !== undefined
					&& item.supplierOrderReferenceItem === undefined) {
					return document.supplierOrderReference;
				}
				return '';
			}).filter((value, index, self) => self.indexOf(value) === index).join(';');
		}
		return document.documentLines.map(item => {
			if (item.supplierOrderReference !== undefined
				&& item.supplierOrderReferenceItem !== undefined) {
				return `${ item.supplierOrderReference }/${ item.supplierOrderReferenceItem }`;
			} else if (item.supplierOrderReference !== undefined
				&& item.supplierOrderReferenceItem === undefined) {
				return item.supplierOrderReference;
			}
			return '';
		}).filter((value, index, self) => self.indexOf(value) === index).join(';');
	case 'shipto':
		if (document.type && (document.type === 'IN' || document.type === 'XCARB')) {
			return document.documentLines.filter(item => item.shiptoName && item.shiptoBusinessId)
				.map(item => `${ item.shiptoName } (${ item.shiptoBusinessId })`)
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		}
		return `${ document.shiptoName } (${ document.shiptoBusinessId })`;
	case 'dispatchAdviceDate':
		if (document.type && (document.type === 'CE' || document.type === 'IN')) {
			return document.documentLines
				.map(item => item.dispatchAdviceDate)
				.filter(Boolean)
				.map(value => moment.utc(value)
					.local().format('YYYY-MM-DD'))
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		}
		return document.dispatchAdviceDate ? moment.utc(document.dispatchAdviceDate).local().format('YYYY-MM-DD') : '';
	case 'dispatchAdviceNumber':
		if (document.type && (document.type === 'DA')) {
			return document.name;
		}
		return document.documentLines.map(item => item.dispatchAdviceNumber)
			.filter((value, index, self) => self.indexOf(value) === index).join(';');
	case 'invoiceNumber':
		if (document.type && (document.type === 'IN')) {
			return document.name;
		}
		return document.invoiceNumber?.toString();
	case 'payer':
		if (document.type && document.type === 'XCARB') {
			return document.documentLines.filter(item => item.payerName && item.payerBusinessId)
				.map(item => item.payerName && item.payerBusinessId ? `${ item.payerName } (${ item.payerBusinessId })`
					: '')
				.filter((value, index, self) => self.indexOf(value) === index).join(';');
		}
		return document.payerName && document.payerBusinessId
			? `${ document.payerName } (${ document.payerBusinessId })` : '';
	}
	return '';
}

const linkToOrderCardFromMultilineCellRendererParams = {
	routerLink(value: string, index: number, params: ICellRendererParams<PartialDocument, unknown>) {
		if (!params.data && !index && !value) {
			return null;
		}
		const supplierOrderAndItemConcat = formatColumnValue('supplierOrderNumber', params.data);
		const customerOrderNumber = resolveStringCellValue(supplierOrderAndItemConcat)[index];
		return {
			commands: [
				'/order-tracking',
				'order',
				'card',
				customerOrderNumber ? customerOrderNumber.split('/')[0] : '',
				customerOrderNumber ? customerOrderNumber.split('/')[1] : ''
			],
		};
	}
};

const linkToDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<PartialDocument, unknown>) {
		if (!params.data) {
			return null;
		}
		const document = params.data;
		let documentType;
		switch (document.type) {
		case 'IN':
			documentType = 'invoice-card';
			break;
		case 'CE':
			documentType = 'certificate-card';
			break;
		case 'DA':
			documentType = 'dispatch-advice-card';
			break;
		case 'OA':
			documentType = 'order-acknowledgment-card';
			break;
		case 'XCARB':
			documentType = 'xcarb-document-card';
			break;
		default:
			throw new Error('unknown document type');
		}

		return {
			commands: [
				'/commercial-documents',
				'cards',
				documentType,
				document.id
			],
		};
	}
};

export class DocumentDatacenterColumnDefinition {
	readonly DOCUMENT_REQUIRED_FIELDS = ['path', 'fileNamePdf', 'proforma'];

	readonly DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<Document>>;

	// eslint-disable-next-line max-lines-per-function
	constructor(
		private readonly translationService: TranslationService,
		private readonly documentService: DocumentService
	) {
		this.DOCUMENT_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<string, CommonColDef<Document>>([
			[
				'name',
				new LinkColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.number'),
					field: 'name',
					colId: 'name',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					minWidth: 150,
					cellRendererParams: linkToDocumentCellRendererParams,
				})
			],
			[
				'type',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.type'),
					field: 'type',
					colId: 'type',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document ? document.typeDescription : '';
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { docFields: ['type', 'typeDescription'], removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'subtype',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.subtype'),
					field: 'subtype',
					colId: 'subtype',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document ? document.subtypeDescription : '';
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { docFields: ['subtype', 'subtypeDescription'], removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'date',
				new DateColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.date'),
					colId: 'date',
					field: 'date',
					cellRendererParams: { inputFormat: 'isoDatetime', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					filterParams: { filterType: 'DOCUMENT_DATE_RANGE_FILTER' },
					suppressHeaderMenuButton: false,
					resizable: true,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'batchNumber',
				new MultiLineColDef<Document>({
					autoHeight: true,
					headerName: this.translationService.getTR('commercial-documents.batch-number'),
					fields: ['documentLines.batchNumber'],
					colId: 'batchNumber',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.map(item => item.batchNumber).join(';') : '';
					},
					minWidth: 150,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'DOCUMENT_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true,
				}),
			],
			[
				'mill',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.mill'),
					fields: ['millCode', 'documentLines.millCode'],
					colId: 'mill',
					valueGetter: params => formatColumnValue('mill', <PartialDocument> params.data),
					filter: DatacenterDropdownFilterComponent,
					filterParams: { filterType: 'DOCUMENT_MILL_DROPDOWN_FILTER',
						docFields: ['millCode', 'millDescription'],
						removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'customer',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.customer'),
					fields: [
						'soldtoName',
						'documentLines.soldtoName',
						'soldtoBusinessId',
						'documentLines.soldtoBusinessId'
					],
					colId: 'customer',
					valueGetter: params => formatColumnValue('customer', <PartialDocument> params.data),
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'DOCUMENT_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'customerOrderNumber',
				new LinkMultiValueColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.customer-order-number'),
					colId: 'customerOrderNumber',
					valueGetter: params => formatColumnValue('customerOrderNumber', <PartialDocument> params.data),
					fields: [
						'customerOrderReference',
						'documentLines.customerOrderReference',
						'documentLines.customerOrderItemReference'
					],
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { filterType: 'DOCUMENT_ORDER_NUMBER_FILTER' },
					suppressHeaderMenuButton: false,
					minWidth: 150,
					cellRendererParams: linkToOrderCardFromMultilineCellRendererParams,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					sortable: true
				}),
			],
			[
				'supplierOrderNumber',
				new LinkMultiValueColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.supplier-order-number'),
					colId: 'supplierOrderNumber',
					valueGetter: params => formatColumnValue('supplierOrderNumber', <PartialDocument> params.data),
					fields: [
						'supplierOrderReference',
						'documentLines.supplierOrderReference',
						'documentLines.supplierOrderReferenceItem'
					],
					minWidth: 150,
					cellRendererParams: linkToOrderCardFromMultilineCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { filterType: 'DOCUMENT_ORDER_NUMBER_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					sortable: true
				})
			],
			[
				'millOrderNumber',
				new LinkMultiValueColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.mill-order-number'),
					colId: 'millOrderNumber',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines ? document.documentLines.map(item => {
							if (item.millOrderReference !== undefined
								&& item.millOrderReferenceItem !== undefined) {
								return `${ item.millOrderReference }/${ item.millOrderReferenceItem }`;
							} else if (item.millOrderReference !== undefined
								&& item.millOrderReferenceItem === undefined) {
								return item.millOrderReference;
							}
							return '';
						}).filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					fields: ['documentLines.millOrderReference', 'documentLines.millOrderReferenceItem'],
					minWidth: 150,
					cellRendererParams: linkToOrderCardFromMultilineCellRendererParams,
					filter: DatacenterOrderNumberFilterComponent,
					filterParams: { filterType: 'DOCUMENT_ORDER_NUMBER_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.ORDER_NUMBER_COLUMN,
					sortable: true
				})
			],
			[
				'steelgrade',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.steel-grade'),
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines ? document.documentLines.map(item => item.steelGrade)
							.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					fields: ['documentLines.steelGrade'],
					colId: 'steelgrade',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'invoicedQuantityUnit',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoiced-quantity-unit'),
					fields: ['documentLines.invoicedQuatityUnit'],
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.map(item => item.invoicedQuatityUnit)
								.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					colId: 'invoicedQuantityUnit',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'invoicedQuantityValue',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoiced-quantity'),
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document?.invoicedQuantity?.toString() ?? '';
					},
					colId: 'invoicedQuantityValue',
					fields: ['invoicedQuantity'],
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'dispatchAdviceNumber',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.dispatch-advice-number'),
					valueGetter: params => formatColumnValue('dispatchAdviceNumber', <PartialDocument> params.data),
					colId: 'dispatchAdviceNumber',
					fields: ['documentLines.dispatchAdviceNumber'],
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'customerClaimReference',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.customer-claim-reference'),
					colId: 'customerClaimReference',
					field: 'customerClaimReference',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'transportReference',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.transport-reference'),
					colId: 'transportReference',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.map(item => item.transportReference)
								.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					fields: ['documentLines.transportReference'],
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'transportMode',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.transport-mode'),
					colId: 'transportMode',
					field: 'transportMode',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document ? document.transportModeDescription : '';
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { filterOptions: ['contains'],
						docFields: ['transportMode', 'transportModeDescription'] },
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'orderAcknowledgementDate',
				new DateColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.order-acknowledgement-date'),
					colId: 'orderAcknowledgementDate',
					field: 'orderAcknowledgementDate',
					minWidth: 100,
					maxWidth: 160,
					width: 100,
					cellRendererParams: { inputFormat: 'isoDatetime', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					filterParams: { filterType: 'DOCUMENT_DATE_RANGE_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					resizable: true
				})
			],
			[
				'receiptDocumentDate',
				new DateColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.received-by-steeluser'),
					colId: 'receiptDocumentDate',
					field: 'receiptDocumentDate',
					minWidth: 100,
					maxWidth: 160,
					width: 100,
					cellRendererParams: { inputFormat: 'isoDatetime', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					filterParams: { filterType: 'DOCUMENT_DATE_RANGE_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					resizable: true
				})
			],
			[
				'rrpType',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('doc-xcarb.rrpType'),
					colId: 'rrpType',
					field: 'rrpType',
					minWidth: 100,
					maxWidth: 160,
					width: 100,
					filter: DatacenterTextFilterComponent,
					sortable: true,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					resizable: true
				})
			],
			[
				'rrpDocumentLink',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('doc-xcarb.rrpDownloadLink'),
					colId: 'rrpDocumentLink',
					field: 'rrpDocumentLink',
					minWidth: 100,
					maxWidth: 160,
					width: 100,
					sortable: true,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					resizable: true
				})
			],
			[
				'amount',
				new NumericColDef<Document>({
					headerName: this.translationService.getTR('doc-xcarb.amount'),
					colId: 'amount',
					minWidth: 100,
					maxWidth: 160,
					width: 100,
					field: 'amount',
					filter: DatacenterNumberFilterComponent,
					sortable: true,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
				})
			],
			[
				'endQuarter',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('doc-xcarb.endquarter'),
					colId: 'endQuarter',
					fields: ['documentLines.endQuarter'],
					filterParams: { filterOptions: ['contains'] },
					minWidth: 100,
					maxWidth: 160,
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.map(item => item.endQuarter)
								.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					width: 100,
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'customerArticleReference',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.customer-article-reference'),
					colId: 'customerArticleReference',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.map(item => item.customerArticleReference)
								.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					fields: ['documentLines.customerArticleReference'],
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'endUser',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.enduser'),
					colId: 'endUser',
					valueGetter: params => {
						const document = params.data;
						return document?.documentLines
							.map(item => item.enduserName && item.enduserBusinessId
								? `${ item.enduserName } (${ item.enduserBusinessId })` : '')
							.filter((value, index, self) => self.indexOf(value) === index).join(';');
					},
					fields: ['documentLines.enduserName', 'documentLines.enduserBusinessId'],
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'payer',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.payer'),
					colId: 'payer',
					fields: [
						'payerName',
						'documentLines.payerName',
						'payerBusinessId',
						'documentLines.payerBusinessId'
					],
					valueGetter: params => formatColumnValue('payer', params.data),
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'invoiceAmount',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoice-amount'),
					colId: 'invoiceAmount',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document?.invoiceAmount?.toString() ?? '';
					},
					fields: ['invoiceAmount'],
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'invoiceAmountWithoutVat',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoice-amount-without-vat'),
					colId: 'invoiceAmountWithoutVat',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document?.invoiceAmountWithoutVat?.toString() ?? '';
					},
					fields: ['invoiceAmountWithoutVat'],
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'invoiceTaxAmount',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoice-tax-amount'),
					colId: 'invoiceTaxAmount',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document?.invoiceTaxAmount?.toString() ?? '';
					},
					fields: ['invoiceTaxAmount'],
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'shipto',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.shipto'),
					colId: 'shipto',
					fields: [
						'shiptoName',
						'documentLines.shiptoName',
						'shiptoBusinessId',
						'documentLines.shiptoBusinessId'
					],
					valueGetter: params => formatColumnValue('shipto', <PartialDocument> params.data),
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'],
						filterType: 'DOCUMENT_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'supplierClaimReference',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.supplier-claim-reference'),
					colId: 'supplierClaimReference',
					field: 'supplierClaimReference',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'dispatchAdviceDate',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.dispatch-advice-date'),
					colId: 'dispatchAdviceDate',
					fields: ['dispatchAdviceDate', 'documentLines.dispatchAdviceDate'],
					valueGetter: params => formatColumnValue('dispatchAdviceDate', <PartialDocument> params.data),
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					filterParams: { filterType: 'DOCUMENT_DATE_RANGE_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					resizable: true,
					valueType: MultiLineValueTypes.DATE,
					sortable: true
				})
			],
			[
				'invoiceCurrency',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoice-currency'),
					colId: 'invoiceCurrency',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document?.invoiceCurrency?.toString() ?? '';
					},
					fields: ['invoiceCurrency'],
					filter: DatacenterTextFilterComponent,
					filterParams: { filterOptions: ['contains'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'orderedQuantity',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.ordered-quantity'),
					colId: 'orderedQuantity',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines
							? document.documentLines.filter(item => item.orderedQuantity !== undefined)
								.map(item => item.orderedQuantity).join(';') : '';
					},
					fields: ['documentLines.orderedQuantity'],
					filter: DatacenterNumberFilterComponent,
					cellRendererParams: { numericFormat: 'weight' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.NUMBER_COLUMN,
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'heatNumber',
				new MultiLineColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.heat-number'),
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						return document && document.documentLines ? document.documentLines.map(item => item.heatNumber)
							.filter((value, index, self) => self.indexOf(value) === index).join(';') : '';
					},
					fields: ['documentLines.heatNumber'],
					colId: 'heatNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'invoiceNumber',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.invoice-number'),
					field: 'invoiceNumber',
					colId: 'invoiceNumber',
					valueGetter: params => formatColumnValue('invoiceNumber', <PartialDocument> params.data),
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'paymentDueDate',
				new DateColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.payment-due-date'),
					colId: 'paymentDueDate',
					field: 'paymentDueDate',
					cellRendererParams: { inputFormat: 'isoDatetime', outputFormat: 'date' },
					filter: DatacenterDateRangeFilterComponent,
					filterParams: { filterType: 'DOCUMENT_DATE_RANGE_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					resizable: true
				})
			],
			[
				'payerVatNumber',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.payer-vat-number'),
					field: 'payerVatNumber',
					colId: 'payerVatNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'proforma',
				new CommonColDef<Document>({
					headerName: this.translationService.getTR('commercial-documents.proforma'),
					field: 'proforma',
					colId: 'proforma',
					valueGetter: params => {
						const document = <PartialDocument | undefined> params.data;
						if (!document) {
							return '';
						}
						if (document.proforma === 'true') {
							return this.translationService.getTR('global.yes');
						}
						if (document.proforma === 'false') {
							return this.translationService.getTR('global.no');
						}
						return '';
					},
					filter: DatacenterDropdownFilterComponent,
					filterParams: { docFields: ['proforma', 'proforma'], removeNoValueFilter: true },
					suppressHeaderMenuButton: false,
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'pdf',
				new IconRedirectColDef<Document>({
					headerClass: ['copo-column-hide'],
					colId: 'pdf',
					headerName: this.translationService.getTR('datacenter.documents'),
					cellRendererParams: {
						linkData: (params: ICellRendererParams<PartialDocument, unknown>) => {
							const { data } = params;
							if (!data || !data.id || !data.name) {
								return undefined;
							}
							return {
								url: this.documentService
									.getDownloadDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					},
					sortable: false,
				})
			]
		]);
	}
}

export const DEFAULT_VIEW_DOCUMENT_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'name',
			pinned: 'left'
		},
		{
			colId: 'type',
			pinned: 'left'
		},
		{
			colId: 'subtype',
			pinned: 'left'
		},
		{
			colId: 'date',
			pinned: 'left'
		},
		{
			colId: 'customer'
		},
		{
			colId: 'shipto'
		},
		{
			colId: 'mill'
		},
		{
			colId: 'supplierOrderNumber'
		},
		{
			colId: 'customerOrderNumber'
		},
		{
			colId: 'batchNumber'
		},
		{
			colId: 'pdf'
		}

	],
	sortModel: [
		{
			colId: 'date',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'document',
	isDefault: true,
	name: 'Default',
	id: -99
};
